/* eslint-disable @typescript-eslint/no-explicit-any */
import { assign, createMachine, send, } from "xstate";
import { assert } from "@renproject/utils";
import { log } from "xstate/lib/actions";
import { isMinted, isOpen, } from "../types/mint";
export var GatewayStates;
(function (GatewayStates) {
    GatewayStates["RESTORING"] = "restoring";
    GatewayStates["CREATING"] = "creating";
    GatewayStates["ERROR_CREATING"] = "srcInitializeError";
    GatewayStates["LISTENING"] = "listening";
    GatewayStates["COMPLETED"] = "completed";
})(GatewayStates || (GatewayStates = {}));
export const buildMintContextWithMap = (params) => {
    const from = params.fromChainMap[params.tx.sourceChain];
    const to = params.toChainMap[params.tx.destChain];
    const constructed = {
        tx: params.tx,
        to,
        sdk: params.sdk,
        from,
    };
    return constructed;
};
/**
 * An Xstate machine that, when given a serializable [[GatewaySession]] tx,
 * will instantiate a RenJS LockAndMint session, provide a gateway address,
 * listen for deposits, and request a signature once a deposit has reached
 * the appropriate number of confirmations.
 *
 * Given the same [[GatewaySession]] parameters, as long as the tx has not
 * expired, the machine will restore the transaction to the appropriate
 * state and enable the completion of in-progress minting transactions.
 *
 * The machine allows for multiple deposits to be detected; it is up to the
 * developer to decide if a detected deposit should be signed or rejected.
 * See `/demos/simpleMint.ts` for example usage.
 */
export const buildMintMachine = () => createMachine({
    id: "RenVMGatewaySession",
    initial: "restoring",
    states: {
        restoring: {
            entry: [
                send("RESTORE"),
                assign({
                    mintRequests: (_c, _e) => [],
                    depositMachines: (_ctx, _evt) => ({}),
                }),
            ],
            meta: { test: async () => { } },
            on: {
                RESTORE: [
                    {
                        target: "completed",
                        cond: "isExpired",
                    },
                    {
                        target: "listening",
                        cond: "isCreated",
                    },
                    {
                        target: "creating",
                    },
                ],
            },
        },
        creating: {
            meta: {
                test: (_, state) => {
                    assert(!state.context.tx.gatewayAddress ? true : false, "Gateway address should not be initialized");
                },
            },
            invoke: {
                src: "txCreator",
                onDone: {
                    target: "listening",
                    actions: assign({
                        tx: (_context, evt) => (Object.assign({}, evt.data)),
                    }),
                },
                onError: {
                    target: "srcInitializeError",
                    actions: [
                        assign({
                            tx: (context, evt) => {
                                const newTx = Object.assign(Object.assign({}, context.tx), { error: evt.data || true });
                                return newTx;
                            },
                        }),
                        log((_ctx, evt) => evt.data, "ERROR"),
                    ],
                },
            },
        },
        srcInitializeError: {
            meta: {
                test: (_, state) => {
                    assert(state.context.tx.error ? true : false, "Error must exist");
                },
            },
        },
        listening: {
            meta: {
                test: (_, state) => {
                    assert(state.context.tx.gatewayAddress ? true : false, "GatewayAddress must exist");
                },
            },
            invoke: {
                src: "depositListener",
            },
            on: {
                EXPIRED: "completed",
                // once we have ren-js listening for deposits,
                // start the statemachines to determine deposit states
                LISTENING: { actions: "depositMachineSpawner" },
                ERROR_LISTENING: {
                    target: "srcInitializeError",
                    actions: [
                        assign({
                            tx: (context, evt) => {
                                const newTx = Object.assign(Object.assign({}, context.tx), { error: evt.data || true });
                                return newTx;
                            },
                        }),
                        log((_ctx, evt) => evt.data, "ERROR"),
                    ],
                },
                // forward messages from child machines to renjs listeners
                RESTORE: [
                    {
                        cond: "isPersistedDeposit",
                        actions: "forwardEvent",
                    },
                    {
                        actions: [
                            assign({
                                tx: ({ tx }, e) => {
                                    if (!e.data.sourceTxHash)
                                        return tx;
                                    return Object.assign(Object.assign({}, tx), { transactions: Object.assign(Object.assign({}, tx.transactions), { [e.data.sourceTxHash]: e.data }) });
                                },
                            }),
                            "spawnDepositMachine",
                            "forwardEvent",
                        ],
                    },
                ],
                SETTLE: {
                    actions: "forwardEvent",
                },
                SIGN: {
                    actions: "forwardEvent",
                },
                MINT: {
                    actions: "forwardEvent",
                },
                // Send messages to child machines
                RESTORED: {
                    actions: "routeEvent",
                },
                CLAIM: { actions: "routeEvent" },
                CONFIRMATION: { actions: "routeEvent" },
                CONFIRMED: { actions: "routeEvent" },
                ERROR: { actions: "routeEvent" },
                SIGN_ERROR: { actions: "routeEvent" },
                REVERTED: { actions: "routeEvent" },
                SUBMIT_ERROR: { actions: "routeEvent" },
                SIGNED: { actions: "routeEvent" },
                SUBMITTED: { actions: "routeEvent" },
                ACKNOWLEDGE: { actions: "routeEvent" },
                CLAIMABLE: {
                    actions: assign({
                        mintRequests: (context, evt) => {
                            var _a;
                            const oldRequests = context.mintRequests || [];
                            const newRequest = (_a = evt.data) === null || _a === void 0 ? void 0 : _a.sourceTxHash;
                            if (!newRequest) {
                                return oldRequests;
                            }
                            if (oldRequests.includes(newRequest)) {
                                return oldRequests;
                            }
                            return [...oldRequests, newRequest];
                        },
                        tx: (context, evt) => {
                            if (evt.data.sourceTxHash) {
                                context.tx.transactions[evt.data.sourceTxHash] = evt.data;
                            }
                            return context.tx;
                        },
                    }),
                },
                // We only complete when expiring
                // DEPOSIT_COMPLETED: {
                //     target: "completed",
                //     cond: "isCompleted",
                // },
                DEPOSIT_UPDATE: [
                    {
                        actions: [
                            assign({
                                mintRequests: (ctx, evt) => {
                                    var _a;
                                    // check if completed
                                    if (isMinted(evt.data)) {
                                        return (((_a = ctx.mintRequests) === null || _a === void 0 ? void 0 : _a.filter((x) => x !==
                                            evt.data
                                                .sourceTxHash)) || []);
                                    }
                                    else {
                                        return ctx.mintRequests;
                                    }
                                },
                                tx: (context, evt) => {
                                    if (evt.data.sourceTxHash) {
                                        context.tx.transactions[evt.data.sourceTxHash] = evt.data;
                                    }
                                    return context.tx;
                                },
                            }),
                            send((_, evt) => {
                                return {
                                    type: "UPDATE",
                                    hash: evt.data.sourceTxHash,
                                    data: evt.data,
                                };
                            }, {
                                to: (_ctx) => "depositListener",
                            }),
                        ],
                    },
                ],
                DEPOSIT: {
                    cond: "isNewDeposit",
                    actions: [
                        assign({
                            tx: (context, evt) => {
                                // Replace the transaction with the newly
                                // detected one; the listener will provide
                                // persisted data if it is already present
                                return Object.assign(Object.assign({}, context.tx), { transactions: Object.assign(Object.assign({}, context.tx.transactions), { [evt.data.sourceTxHash]: evt.data }) });
                            },
                        }),
                        "spawnDepositMachine",
                    ],
                },
            },
        },
        completed: {
            meta: {
                test: (_, state) => {
                    if (state.context.depositListenerRef) {
                        throw Error("Deposit listener has not been cleaned up");
                    }
                },
            },
        },
    },
}, {
    guards: {
        isPersistedDeposit: (ctx, evt) => {
            const depositEvt = evt;
            if (!depositEvt.data)
                return false;
            return (ctx.tx.transactions || {})[depositEvt.data.sourceTxHash]
                ? true
                : false;
        },
        isNewDeposit: (ctx, evt) => {
            const depositEvt = evt;
            if (!depositEvt.data)
                return false;
            return !(ctx.depositMachines || {})[depositEvt.data.sourceTxHash];
        },
        isExpired: ({ tx }) => tx.expiryTime < new Date().getTime(),
        isCreated: ({ tx }) => isOpen(tx),
    },
});
//# sourceMappingURL=mint.js.map